// material-ui
import { alpha } from "@mui/material/styles";

import { THEME_COLOR_CONFIG } from "config/themeColors";

// ==============================|| CUSTOM FUNCTION - WITH ALPHA ||============================== //

/**
 * Converts a hex color string to an RGB channel string ("r g b").
 *
 * @param hex - The hex color string (e.g. "#C8FAD6", "#FFF", "#FF00FFAA").
 * @returns The RGB channel string (e.g. "200 250 214").
 * @throws {Error} If the input is not a valid hex color.
 */
export function hexToRgbChannel(hex) {
  let cleaned = hex.replace(/^#/, "");

  if (cleaned.length === 3) {
    cleaned = cleaned
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (cleaned.length === 4) {
    cleaned = cleaned
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if (cleaned.length !== 6 && cleaned.length !== 8) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);

  return `${r} ${g} ${b}`;
}

export function extendPaletteWithChannels(palette) {
  const result = { ...palette };

  Object.entries(palette).forEach(([k, v]) => {
    if (typeof v === "string" && v.startsWith("#")) {
      result[`${k}Channel`] = hexToRgbChannel(v);
    } else if (typeof v === "object" && v !== null) {
      result[k] = extendPaletteWithChannels(v);
    }
  });

  return result;
}

export function withAlpha(color, opacity) {
  // Case 1: normal color (hex, rgb, hsl…)
  if (/^#|rgb|hsl|color/i.test(color)) {
    return alpha(color, opacity);
  }

  // Case 2: CSS Var: var(--mui-palette-xxx) or var(--palette-xxx, #hex)
  if (color.startsWith("var(")) {
    // inject "Channel" *before the closing parenthesis of the var name only*
    return color
      .replace(/(--[a-zA-Z0-9-]+)(.*)\)/, `$1Channel$2)`)
      .replace(/^var\((.+)\)$/, `rgba(var($1) / ${opacity})`);
  }

  // Fallback
  return color;
}

/** Dark list/table zebra (AG Grid style): odd = lighter charcoal, even = darker. */
export const DARK_ZEBRA_ROW_COLORS = {
  ...THEME_COLOR_CONFIG.surface.table.zebraDark,
};

/**
 * MUI colorSchemes use data-color-scheme; palette.mode alone is not always "dark".
 *
 * @param {import('@mui/material/styles').Theme} [theme]
 */
export function detectDarkMode(theme) {
  if (theme?.palette?.mode === "dark") return true;
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  const schemeAttr =
    root.getAttribute("data-mui-color-scheme") ||
    root.getAttribute("data-color-scheme") ||
    "";
  if (schemeAttr.toLowerCase().includes("dark")) return true;
  const bodyClasses = document.body?.className || "";
  return bodyClasses.toLowerCase().includes("dark");
}

/**
 * @param {import('@mui/material/styles').Theme} theme
 * @param {string} [colorSchemeMode] from useColorScheme().mode ('light' | 'dark' | 'system')
 */
export function isDarkColorScheme(theme, colorSchemeMode) {
  if (colorSchemeMode === "dark") return true;
  if (colorSchemeMode === "light") return false;
  if (detectDarkMode(theme)) return true;
  return theme?.palette?.mode === "dark";
}

/**
 * Returns theme-aware zebra row colors for tables.
 *
 * @param {import('@mui/material/styles').Theme} theme
 * @param {string} [colorSchemeMode] from useColorScheme().mode
 */
export function getZebraRowColors(theme, colorSchemeMode) {
  if (isDarkColorScheme(theme, colorSchemeMode)) {
    return { ...DARK_ZEBRA_ROW_COLORS };
  }

  return {
    odd: withAlpha(
      theme.palette.primary.main,
      THEME_COLOR_CONFIG.surface.table.zebraOddAlphaLight,
    ),
    even: theme.palette.background.paper,
  };
}

/**
 * @param {number} rowIndex 0-based row index (across page for paginated lists).
 */
export function getZebraStripeBackground(rowIndex, theme, colorSchemeMode) {
  const colors = getZebraRowColors(theme, colorSchemeMode);
  return rowIndex % 2 === 0 ? colors.odd : colors.even;
}

/**
 * Apply to MuiTable `sx` — sets opaque stripe backgrounds on body cells.
 *
 * @param {import('@mui/material/styles').Theme} theme
 * @param {string} [colorSchemeMode]
 */
export function zebraTableBodyCellSx(theme, colorSchemeMode) {
  const colors = getZebraRowColors(theme, colorSchemeMode);
  const isDark = isDarkColorScheme(theme, colorSchemeMode);

  return {
    "& .MuiTableBody-root .MuiTableRow-root": {
      borderBottom: isDark ? "none" : undefined,
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root":
      {
        backgroundColor: colors.odd,
      },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root":
      {
        backgroundColor: colors.even,
      },
  };
}
